import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import { Button } from '../components/ui/button';
import { OfficialBadge, OfficialLegalNotice } from '../components/OfficialBadge';
import { PrintButton } from '../components/PrintButton';
import { cn } from '../lib/utils';
import useSeo from '../hooks/useSeo';
import {
  ArrowRight, BookOpen, Scale, ExternalLink, Target, GraduationCap,
  Route, Flag, UserCheck, Users, Cog, Shield, Leaf, HeartPulse, Calendar,
  CheckCircle2, FileText, ChevronDown, AlertTriangle, HelpCircle, ClipboardCheck,
  Bike
} from 'lucide-react';
import { LEXIQUE } from '../data/lexique';
import { Link } from 'react-router-dom';

/**
 * Page "Programme officiel ETM" — référence pédagogique publique.
 *
 * Objectif : transparence totale sur les sources et la structure du programme
 * officiel de l'Épreuve Théorique Moto, pour rassurer les élèves et prouver
 * la conformité (sans affiliation à un éditeur tiers).
 */

const THEMES = [
  {
    code: 'R', color: '#cf2031', soft: '#fff1f2', icon: Route,
    name: 'La route',
    target: 'Infrastructures, signalisation, priorités, trajectoires',
    subthemes: [
      { code: 'R.1', label: 'Signalisation', items: ['Panneaux danger', 'Intersections', 'Marquages au sol'] },
      { code: 'R.2', label: 'Intersections & Priorités', items: ['Priorité à droite', 'Stop / Cédez', 'Carrefours à sens giratoire'] },
      { code: 'R.3', label: 'Limitations de vitesse', items: ['Ville 50 · Route 80 · Autoroute 130', 'Pluie / probatoire', 'Zones 30'] },
      { code: 'R.4', label: 'Types de routes', items: ['Voies rapides', 'Voies réservées', 'Zones de rencontre'] },
    ],
  },
  {
    code: 'C', color: '#1d4ed8', soft: '#eef5ff', icon: Flag,
    name: 'La circulation routière',
    target: 'Règles dynamiques, manœuvres, comportements en circulation',
    subthemes: [
      { code: 'C.1', label: 'Règles de circulation', items: ['Tenir sa droite', 'Distances de sécurité', 'Clignotants'] },
      { code: 'C.2', label: 'Dépassement', items: ['Conditions', 'Interdictions', 'Dépassement à droite (exceptions)'] },
      { code: 'C.3', label: 'Changement de direction', items: ['Signaler 30 m avant', 'Vérifier angle mort', 'Insertion'] },
      { code: 'C.4', label: 'Circulation inter-files (CIF)', items: ['Zones autorisées (Île-de-France, Gironde, etc.)', 'Vitesse max 50 km/h'] },
      { code: 'C.5', label: 'Arrêt & stationnement', items: ['Places réservées', 'Stationnement moto ville'] },
    ],
  },
  {
    code: 'L', color: '#7c3aed', soft: '#f4effd', icon: UserCheck,
    name: 'Le conducteur',
    target: 'Aptitude, permis, substances, fatigue',
    subthemes: [
      { code: 'L.1', label: 'Permis (A1, A2, AM)', items: ['Âges minimums', 'Formation obligatoire', 'Validité'] },
      { code: 'L.2', label: 'Alcool & stupéfiants', items: ['0,2 g/L probatoire · 0,5 g/L standard', 'Sanctions'] },
      { code: 'L.3', label: 'Fatigue & santé', items: ['Signes', 'Pauses obligatoires', 'Visibilité motard'] },
      { code: 'L.4', label: 'Âge & formation', items: ['ASSR 2', 'Permis progressif A2 → A'] },
    ],
  },
  {
    code: 'U', color: '#db2777', soft: '#fdf2f8', icon: Users,
    name: 'Les autres usagers',
    target: 'Partage de la route, vulnérables, poids lourds',
    subthemes: [
      { code: 'U.1', label: 'Piétons', items: ['Priorité passage piéton', 'Zones 30'] },
      { code: 'U.2', label: 'Cyclistes & EDPM', items: ['Distance 1 m ville / 1,5 m hors ville', 'Pistes cyclables'] },
      { code: 'U.3', label: 'Angles morts', items: ['Camions', 'Bus', 'Véhicules rallongés'] },
      { code: 'U.4', label: 'Poids lourds / Bus', items: ['Ne jamais longer à droite', 'Temps de réaction'] },
      { code: 'U.5', label: 'Enfants', items: ['Vigilance abords écoles', 'Comportement imprévisible'] },
    ],
  },
  {
    code: 'D', color: '#0891b2', soft: '#ecfeff', icon: BookOpen,
    name: 'Les notions diverses',
    target: 'Administratif, sanctions, documents obligatoires',
    subthemes: [
      { code: 'D.1', label: 'Assurance', items: ['RC obligatoire', 'Vignette verte', 'Attestation'] },
      { code: 'D.2', label: 'Carte grise', items: ['Changement d\'adresse 30 j', 'Co-titulaire'] },
      { code: 'D.3', label: 'Contrôle technique moto (CT2RM)', items: ['1er CT à 5 ans, puis tous les 3 ans', 'Vente : PV < 6 mois — Décret 23/10/2023'] },
      { code: 'D.4', label: 'Sanctions & points', items: ['Barème permis', 'Suspension / invalidation'] },
      { code: 'D.5', label: 'Constat amiable', items: ['Comment remplir', 'Quand le signer'] },
    ],
  },
  {
    code: 'M', color: '#f59e0b', soft: '#fff7ed', icon: Cog,
    name: 'La mécanique & équipements',
    target: 'Check-list moto, entretien courant, vérifications',
    subthemes: [
      { code: 'M.1', label: 'Pneus', items: ['Pression à froid', 'Usure TWI', 'Contrôle mensuel'] },
      { code: 'M.2', label: 'Freins', items: ['Frein avant 70% efficacité', 'Combiné ABS'] },
      { code: 'M.3', label: 'Éclairage', items: ['Feux de jour moto obligatoires', 'Fonctionnement clignotants'] },
      { code: 'M.4', label: 'Transmission', items: ['Chaîne : tension, graissage', 'Cardan'] },
      { code: 'M.5', label: 'Entretien courant', items: ['Niveaux huile/liquide', 'Durite carburant'] },
    ],
  },
  {
    code: 'P', color: '#18794e', soft: '#eaf8ef', icon: Shield,
    name: 'Les équipements de protection',
    target: 'EPI (Équipements de Protection Individuelle) motard',
    subthemes: [
      { code: 'P.1', label: 'Casque', items: ['Homologué ECE 22.06', 'Jugulaire attachée', 'Étiquette visible'] },
      { code: 'P.2', label: 'Gants CE', items: ['Obligatoires depuis nov. 2016', 'Normalisés CE'] },
      { code: 'P.3', label: 'Blouson & dorsale', items: ['Recommandés fortement', 'Normes CE'] },
      { code: 'P.4', label: 'Gilet jaune', items: ['Obligatoire en panne / arrêt hors agglo'] },
      { code: 'P.5', label: 'Bottes moto', items: ['Renforcées cheville', 'Semelle antidérapante'] },
    ],
  },
  {
    code: 'E', color: '#16a34a', soft: '#f0fdf4', icon: Leaf,
    name: "L'environnement",
    target: 'Pollution, bruit, éco-conduite, ZFE',
    subthemes: [
      { code: 'E.1', label: 'Pollution', items: ['Émissions CO₂', 'Norme Euro 5'] },
      { code: 'E.2', label: 'Bruit & pot homologué', items: ['Décibels max', 'Amendes silencieux non homologué'] },
      { code: 'E.3', label: 'Éco-conduite 2 roues', items: ['Anticipation', 'Régime moteur'] },
      { code: 'E.4', label: 'ZFE', items: ['Crit\'Air 2R', 'Restrictions grandes villes'] },
    ],
  },
  {
    code: 'S', color: '#b91c1c', soft: '#fff1f2', icon: HeartPulse,
    name: 'Les premiers secours',
    target: 'PAS (Protéger · Alerter · Secourir) adapté motard',
    subthemes: [
      { code: 'S.1', label: 'Protéger', items: ['Éviter le suraccident', 'Baliser la zone'] },
      { code: 'S.2', label: 'Alerter', items: ['112 / 18 / 15', 'Message type'] },
      { code: 'S.3', label: 'Secourir', items: ['Ne pas déplacer si pas de danger', 'PLS'] },
      { code: 'S.4', label: 'Casque & PLS', items: ['Retirer uniquement si arrêt respiratoire', 'Technique à 2 secouristes idéal'] },
      { code: 'S.5', label: 'Hémorragie', items: ['Compression directe', 'Pas de garrot sauf urgence'] },
    ],
  },
];

const SOURCES = [
  {
    title: "Arrêté du 29 novembre 2019",
    subtitle: "Programme officiel du code moto",
    detail: "Définit le contenu, les thèmes, le format (40 questions, 35 bonnes) et la validité (5 ans).",
    url: "https://www.legifrance.gouv.fr/loda/id/JORFTEXT000039711424/",
  },
  {
    title: "Code de la route",
    subtitle: "Articles L. et R. (Légifrance)",
    detail: "Base légale complète : articles R411 à R431 notamment pour la filière moto.",
    url: "https://www.legifrance.gouv.fr/codes/texte_lc/LEGITEXT000006074228/",
  },
  {
    title: "Sécurité routière",
    subtitle: "Site officiel de la DSCR",
    detail: "Fiches permis moto A1/A2/A, recommandations, statistiques ONISR.",
    url: "https://www.securite-routiere.gouv.fr/les-differents-permis-de-conduire/permis-moto-categorie",
  },
  {
    title: "REMC",
    subtitle: "Référentiel pour l'Éducation à la Mobilité Citoyenne",
    detail: "Programme et modalités officiels de la formation à la conduite (arrêté du 23 avril 2012, publié le 27/05/2013).",
    url: "https://www.legifrance.gouv.fr/loda/id/JORFTEXT000027506891/",
  },
];

// Lexique pédagogique — données partagées (cf. /src/data/lexique.js)
// → utilisé sur cette page ET sur la page dédiée /lexique
// (les imports LEXIQUE / LEXIQUE_CATEGORIES sont gérés en haut du fichier).

const LexiqueCard = ({ entry }) => {
  const [open, setOpen] = useState(false);
  const Icon = entry.icon;
  return (
    <div
      className="mc-card p-5 hover:shadow-lg transition-shadow relative overflow-hidden"
      data-testid={`lexique-${entry.term.toLowerCase().replace(/[^a-z]+/g, '-')}`}
    >
      <div
        className="absolute -top-10 -right-10 w-24 h-24 rounded-full blur-2xl opacity-15"
        style={{ background: entry.color }}
      />
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="md:pointer-events-none w-full text-left flex items-start gap-3 mb-3 relative"
        aria-expanded={open}
      >
        <div
          className="w-11 h-11 rounded-2xl flex items-center justify-center flex-shrink-0"
          style={{ background: entry.soft, color: entry.color }}
        >
          <Icon className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-base sm:text-lg font-black leading-tight">{entry.term}</h3>
          <p className="text-xs text-muted-foreground mt-1 line-clamp-2 md:line-clamp-none">
            {entry.definition}
          </p>
        </div>
        <ChevronDown className={cn('md:hidden w-4 h-4 transition-transform mt-1.5 flex-shrink-0', open && 'rotate-180')} />
      </button>

      <div className={cn('md:block relative', open ? 'block' : 'hidden')}>
        <div className="text-[10px] font-black uppercase tracking-widest text-muted-foreground mb-1.5">
          Règles & repères
        </div>
        <ul className="space-y-1.5 mb-3">
          {entry.rules.map((r, i) => (
            <li key={i} className="flex items-start gap-2 text-sm">
              <CheckCircle2 className="w-3.5 h-3.5 flex-shrink-0 mt-1" style={{ color: entry.color }} />
              <span className="leading-relaxed text-foreground/85">{r}</span>
            </li>
          ))}
        </ul>
        {entry.distinction && (
          <div className="rounded-lg p-2.5 text-xs leading-relaxed flex items-start gap-2" style={{ background: `${entry.color}10`, borderLeft: `2px solid ${entry.color}` }}>
            <span className="font-black uppercase tracking-widest text-[10px] flex-shrink-0" style={{ color: entry.color }}>
              Distinction
            </span>
            <span className="text-foreground/80">{entry.distinction}</span>
          </div>
        )}
      </div>
    </div>
  );
};


/**
 * ThemeCard — carte thème avec sous-thèmes repliables sur mobile, visibles sur desktop.
 */
const ThemeCard = ({ t, Icon, count, navigate }) => {
  const [open, setOpen] = useState(false);
  return (
    <div
      className="mc-card p-5 hover:shadow-lg transition-shadow relative overflow-hidden"
      data-testid={`programme-theme-${t.code}`}
    >
      <div
        className="absolute -top-10 -right-10 w-24 h-24 rounded-full blur-2xl opacity-20"
        style={{ background: t.color }}
      />
      <div className="relative">
        {/* Header cliquable sur mobile */}
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="md:pointer-events-none w-full text-left flex items-start gap-3 mb-3"
          data-testid={`programme-theme-toggle-${t.code}`}
          aria-expanded={open}
        >
          <div
            className="w-11 h-11 rounded-2xl flex items-center justify-center flex-shrink-0"
            style={{ background: t.soft, color: t.color }}
          >
            <Icon className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span
                className="inline-flex items-center justify-center w-6 h-6 rounded-full text-[10px] font-black text-white"
                style={{ background: t.color }}
              >
                {t.code}
              </span>
              <h3 className="text-base sm:text-lg font-black">{t.name}</h3>
            </div>
            <p className="text-xs text-muted-foreground mt-1">{t.target}</p>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <span
              className={cn(
                'text-[10px] font-black uppercase tracking-widest rounded-full bg-muted text-foreground/80 px-2 py-1 tabular-nums min-w-[36px] text-center',
                count > 0 ? 'opacity-100' : 'opacity-0',
              )}
              aria-hidden={count === 0}
            >
              {count > 0 ? `${count} Q` : '— Q'}
            </span>
            <ChevronDown className={cn('md:hidden w-4 h-4 transition-transform', open && 'rotate-180')} />
          </div>
        </button>

        {/* Sous-thèmes + CTA — repliables sur mobile */}
        <div className={cn('md:block', open ? 'block' : 'hidden')}>
          <ul className="space-y-2 mt-3">
            {t.subthemes.map(st => (
              <li key={st.code} className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: t.color }} />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-bold">
                    <span className="text-xs font-mono mr-1.5" style={{ color: t.color }}>{st.code}</span>
                    {st.label}
                  </div>
                  <div className="text-xs text-muted-foreground leading-relaxed mt-0.5">
                    {st.items.join(' · ')}
                  </div>
                </div>
              </li>
            ))}
          </ul>

          <Button
            onClick={() => navigate('/revision')}
            variant="outline"
            size="sm"
            className="rounded-full mt-4"
            data-testid={`programme-theme-cta-${t.code}`}
          >
            S'entraîner sur ce thème <ArrowRight className="w-3.5 h-3.5 ml-1.5" />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default function ProgrammeETM() {
  const navigate = useNavigate();
  const [themes, setThemes] = useState([]);

  useEffect(() => {
    api.getThemes().then(setThemes).catch(() => setThemes([]));
  }, []);

  const getCount = (code) => themes.find(t => t.code === code)?.question_count || 0;
  const totalQuestions = themes.reduce((a, t) => a + (t.question_count || 0), 0);

  // SEO : JSON-LD FAQPage généré depuis le Lexique pédagogique
  // (chaque entrée = une question/réponse = excellent pour le rich snippets Google)
  const faqJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    'mainEntity': LEXIQUE.slice(0, 20).map((entry) => ({
      '@type': 'Question',
      'name': `Qu'est-ce que ${entry.term} ?`,
      'acceptedAnswer': {
        '@type': 'Answer',
        'text': entry.definition,
      },
    })),
  };

  useSeo({
    title: `Programme officiel du code moto 2026 — 9 thèmes`,
    description: `Programme officiel du code moto 2026 (arrêté du 29 nov. 2019) : ${totalQuestions || 719}+ questions, 9 thèmes, lexique pédagogique. Conforme REMC.`,
    keywords: 'programme code moto, ETM 2026, épreuve théorique moto, thèmes code moto, lexique code moto, CSG carrefour giratoire, P-A-S premiers secours, circulation inter-files, point mort moto, formation 7h, passerelle A1 A2, mention 78 TMAX, REMC',
    path: '/programme-etm',
    jsonLd: faqJsonLd,
  });

  return (
    <div className="space-y-8 sm:space-y-10" data-testid="programme-etm-page">
      {/* Hero */}
      <section className="mc-card p-6 sm:p-10 relative overflow-hidden min-h-[280px] sm:min-h-[340px]" data-testid="programme-hero">
        <div className="absolute -top-20 -right-20 w-64 h-64 rounded-full blur-3xl opacity-20" style={{ background: '#16a34a' }} />
        <div className="relative">
          <OfficialBadge size="lg" />
          <h1 className="text-3xl sm:text-5xl font-black tracking-tight leading-[1.05] mt-4">
            Le programme du<br />
            <span className="text-[hsl(var(--primary))]">code moto en France.</span>
          </h1>
          <p className="text-sm sm:text-lg text-muted-foreground mt-4 max-w-2xl leading-relaxed">
            Retrouve ici la structure exacte du programme officiel défini par l'arrêté du 29 novembre 2019,
            avec les <strong>9 thèmes</strong> et leurs sous-compétences. Nos <span className="tabular-nums">{totalQuestions || 719}</span> questions
            pédagogiques sont rédigées selon ce référentiel.
          </p>
          <p className="text-xs text-muted-foreground mt-3 italic max-w-2xl">
            Code 2 Roues est une plateforme d'apprentissage. Nous ne dispensons pas l'examen officiel — celui-ci se passe uniquement chez un opérateur agréé par l'État.
          </p>
          <div className="flex flex-wrap gap-2 mt-6">
            <Button onClick={() => navigate('/revision')} className="mc-btn-primary" data-testid="cta-revision">
              <Target className="w-4 h-4 mr-1.5" /> Je m'entraîne par thème
            </Button>
            <Button onClick={() => navigate('/parcours')} variant="outline" className="rounded-full" data-testid="cta-parcours">
              <GraduationCap className="w-4 h-4 mr-1.5" /> Voir mon parcours
            </Button>
            <PrintButton label="Imprimer le programme" testid="programme-print-btn" />
          </div>
        </div>
      </section>

      {/* Cadre légal */}
      <section className="mc-card p-5 sm:p-6 border-l-4 border-l-[hsl(var(--primary))]" data-testid="cadre-legal">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 bg-[hsl(var(--primary-soft))] text-[hsl(var(--primary))]">
            <Calendar className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] font-black uppercase tracking-widest text-[hsl(var(--primary))]">
              Cadre légal · en vigueur depuis le 1er mars 2020
            </div>
            <h2 className="text-xl sm:text-2xl font-black mt-1">Un examen théorique obligatoire pour tous</h2>
            <p className="text-sm text-foreground/85 mt-2 leading-relaxed">
              Depuis le <strong>1er mars 2020</strong> (décret n° 2019-1569 du 31 décembre 2019), l'épreuve théorique moto
              est <strong>obligatoire</strong> pour tous les candidats aux permis <strong>A1</strong> et
              <strong> A2</strong>, sans aucune dispense possible. La détention du permis B ou de l'ancien ETG ne dispense plus.
            </p>
            <div className="grid sm:grid-cols-3 gap-3 mt-4">
              {[
                { k: '40', u: 'questions', d: 'Tirage stratifié sur les 9 thèmes' },
                { k: '35', u: '/40 minimum', d: 'Seuil officiel de réussite' },
                { k: '5', u: 'ans', d: 'Durée de validité de l\'épreuve obtenue' },
              ].map((x, i) => (
                <div key={i} className="p-3 rounded-lg border border-border bg-[hsl(var(--panel))]/50">
                  <div className="flex items-baseline gap-1.5">
                    <span className="text-2xl sm:text-3xl font-black text-[hsl(var(--primary))]">{x.k}</span>
                    <span className="text-xs font-bold text-muted-foreground">{x.u}</span>
                  </div>
                  <div className="text-xs text-foreground/70 mt-1">{x.d}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Lexique pédagogique précis — aperçu vers la page dédiée /lexique */}
      <section data-testid="lexique-pedagogique">
        <div className="mb-5 flex items-end justify-between gap-3 flex-wrap">
          <div className="flex-1 min-w-0">
            <div className="text-[10px] font-black uppercase tracking-widest text-[hsl(var(--primary))] mb-1">
              Vocabulaire ECSR · {LEXIQUE.length} termes
            </div>
            <h2 className="text-2xl sm:text-3xl font-black tracking-tight">Lexique pédagogique précis</h2>
            <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
              Les définitions exactes des termes officiels et des concepts clés de la conduite préventive,
              pour éviter toute confusion à l'examen et sur la route.
            </p>
          </div>
          <Link
            to="/lexique"
            className="mc-btn-primary inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-bold whitespace-nowrap"
            data-testid="programme-cta-lexique"
          >
            Voir le lexique complet <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

        {/* Aperçu : 4 termes phares (CSG, P-A-S, point mort, mention 78) */}
        <div className="grid md:grid-cols-2 gap-4">
          {LEXIQUE
            .filter((e) => ['csg', 'pas-premiers-secours', 'point-mort', 'mention-78-tmax'].includes(e.slug))
            .map((entry) => (
              <LexiqueCard key={entry.term} entry={entry} />
            ))}
        </div>

        {/* Footer CTA */}
        <div className="mt-4 text-center">
          <Link
            to="/lexique"
            className="inline-flex items-center gap-1.5 text-sm font-bold text-[hsl(var(--primary))] hover:underline"
            data-testid="programme-link-lexique-bottom"
          >
            + {LEXIQUE.length - 4} autres définitions dans le lexique complet
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      {/* Les 9 thèmes officiels */}
      <section data-testid="themes-officiels">
        <div className="mb-5">
          <div className="text-[10px] font-black uppercase tracking-widest text-[hsl(var(--primary))] mb-1">
            Le référentiel officiel
          </div>
          <h2 className="text-2xl sm:text-3xl font-black tracking-tight">Les 9 thèmes du programme</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Programme défini par l'arrêté du 29 novembre 2019 modifié.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          {THEMES.map(t => {
            const Icon = t.icon;
            const count = getCount(t.code);
            return <ThemeCard key={t.code} t={t} Icon={Icon} count={count} navigate={navigate} />;
          })}
        </div>
      </section>

      {/* Sources officielles */}
      <section data-testid="sources-officielles">
        <div className="mb-5">
          <div className="text-[10px] font-black uppercase tracking-widest text-[hsl(var(--primary))] mb-1">
            Transparence
          </div>
          <h2 className="text-2xl sm:text-3xl font-black tracking-tight">Sources officielles mobilisées</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Code 2 Roues s'appuie exclusivement sur des sources publiques et officielles.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          {SOURCES.map((s, i) => (
            <a
              key={i}
              href={s.url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`source-${i}`}
              className="mc-card mc-card-accent-official p-5 hover:shadow-lg hover:-translate-y-0.5 transition-all group"
            >
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 bg-[#1d4ed8]/12 text-[#1d4ed8]">
                  <Scale className="w-4 h-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <h3 className="font-black text-sm sm:text-base">{s.title}</h3>
                    <ExternalLink className="w-3.5 h-3.5 text-muted-foreground group-hover:text-[#1d4ed8]" />
                  </div>
                  <div className="text-xs font-bold text-[#1d4ed8] mt-0.5">{s.subtitle}</div>
                  <p className="text-xs text-muted-foreground mt-2 leading-relaxed">{s.detail}</p>
                </div>
              </div>
            </a>
          ))}
        </div>
      </section>

      {/* Mention légale détaillée */}
      <OfficialLegalNotice />
    </div>
  );
}
